import { cva } from "class-variance-authority";
import { Star, Radar, Navigation } from "lucide-react";
import { Link, useLocation } from "react-router";
import { cn } from "@/lib/utils";
import { FAVORITES_PATH } from "@/features/stops/favorites-route";

const tabButtonVariants = cva(
  "flex flex-col items-center justify-center gap-1 px-3 py-2 rounded-lg text-xs font-medium transition-all outline-none focus-visible:ring-2 focus-visible:ring-ring",
  {
    variants: {
      active: {
        true: "text-primary bg-primary/10",
        false:
          "text-muted-foreground hover:text-foreground hover:bg-accent active:bg-secondary",
      },
    },
    defaultVariants: {
      active: false,
    },
  },
);

interface TabNavigationProps {
  variant: "clean" | "dark";
  className?: string;
}

const tabs = [
  { path: FAVORITES_PATH, label: "Favorites", icon: Star },
  { path: "/nearby", label: "Nearby", icon: Radar },
  // { path: "/routes", label: "Routes", icon: Route },
  { path: "/track", label: "Track", icon: Navigation },
];

export function TabNavigation({ variant, className }: TabNavigationProps) {
  const location = useLocation();
  const isDark = variant === "dark";

  return (
    <nav
      className={cn(
        // `dark` on the container flips the design-system tokens below to the
        // dark theme without any hardcoded color literals.
        isDark && "dark",
        "fixed bottom-0 left-0 right-0 z-50 h-[calc(var(--tab-navigation-height)+env(safe-area-inset-bottom,0px))] border-t border-border bg-background/80 backdrop-blur-lg pb-safe",
        className,
      )}
      aria-label="Main navigation"
    >
      <div className="grid grid-cols-3 gap-1 px-2 pt-2 pb-2 pl-[calc(0.5rem+env(safe-area-inset-left,0px))] pr-[calc(0.5rem+env(safe-area-inset-right,0px))]">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive =
            location.pathname === tab.path ||
            location.pathname.startsWith(`${tab.path}/`);

          return (
            <Link
              key={tab.path}
              to={tab.path}
              className={cn(tabButtonVariants({ active: isActive }))}
              aria-label={`${tab.label} tab`}
              aria-current={isActive ? "page" : undefined}
            >
              <Icon className="size-6" aria-hidden="true" />
              <span>{tab.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
